import React, { useState } from 'react';
import { X, Building2, Mail, User, MessageSquare, Send, Check, AlertCircle } from 'lucide-react';
import { customerApi } from '../services/customerApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /* The backend maps anything that is not 'desktop-app' onto 'spatial-model'
     (support.controller.ts:2221), so an SDK enquiry is filed as a spatial-model
     one until that line learns about it. Named correctly here regardless. */
  source?: 'spatial-model' | 'desktop-app' | 'sdk';
}

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface SubmissionResult {
  success: boolean;
  inquiryId?: string;
  message?: string;
  error?: string;
}

export default function EnterpriseSolutionModal({ isOpen, onClose, title = 'Create A Free Spatial Model', subtitle = 'Create your first CR3ATE Spatial Model using your own data set', source = 'spatial-model' }: Props) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setStatus('submitting');
    setSubmissionResult(null);

    try {
      const result = await customerApi.sendEnterpriseInquiry({ ...formData, source });

      if (!result.success) {
        throw new Error(result.message || 'Failed to submit enterprise inquiry');
      }

      setSubmissionResult({
        success: true,
        inquiryId: result.data?.inquiryId,
        message: result.message
      });
      setStatus('success');
      setShowSuccessModal(true);

      // Clear form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      
      console.log('Enterprise inquiry submitted successfully:', result.data?.inquiryId);

    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit enterprise inquiry. Please try again.'
      });
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setSubmissionResult(null);
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', company: '', message: '' });
    setErrors({});
    setStatus('idle');
    setSubmissionResult(null);
    setShowSuccessModal(false);
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const isFormValid = formData.name.trim() && formData.email.trim() && 
                     formData.company.trim() && formData.message.trim();
  const isSubmitting = status === 'submitting';

  if (!isOpen) return null;

  // Success Modal
  if (showSuccessModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/80 transition-opacity" />
          
          <div className="relative bg-[#0E0D0C] rounded-2xl shadow-xl max-w-md w-full border border-white/10">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/30">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Thank you for your interest!</h3>
              <p className="text-gray-300 mb-4">
                We'll be in touch shortly
              </p>
              {submissionResult?.inquiryId && (
                <p className="text-xs text-gray-400 mb-4">
                  Reference ID: {submissionResult.inquiryId}
                </p>
              )}
              <button
                onClick={handleSuccessClose}
                className="w-full px-4 py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/90 text-[#161513] rounded-lg hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/80 transition-all font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 transition-opacity" onClick={handleClose} />

        <div className="relative bg-[#0E0D0C] rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto transform transition-all border border-white/10">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h3 className="text-xl font-semibold text-white">{title}</h3>
              <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Banner */}
          {status === 'error' && submissionResult?.error && (
            <div className="mx-6 mt-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-400 mb-1">Unable to Submit Inquiry</h3>
                  <p className="text-sm text-red-300 mb-3">{submissionResult.error}</p>
                  <button 
                    onClick={handleRetry} 
                    className="text-sm bg-red-900/30 text-red-300 px-3 py-1 rounded-md hover:bg-red-900/50 transition-colors border border-red-500/30"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="Enter your name"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border ${errors.name ? 'border-red-500' : 'border-white/20'} text-white focus:outline-none focus:ring-2 focus:ring-[#0CB4E4] focus:border-transparent disabled:opacity-50`}
                    required
                  />
                </div>
                {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="name@company.com"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border ${errors.email ? 'border-red-500' : 'border-white/20'} text-white focus:outline-none focus:ring-2 focus:ring-[#0CB4E4] focus:border-transparent disabled:opacity-50`}
                    required
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white mb-2">
                  Company <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="Company name"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border ${errors.company ? 'border-red-500' : 'border-white/20'} text-white focus:outline-none focus:ring-2 focus:ring-[#0CB4E4] focus:border-transparent disabled:opacity-50`}
                    required
                  />
                </div>
                {errors.company && <p className="mt-1 text-sm text-red-400">{errors.company}</p>}
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="Please provide us more information about your business and your interest in our product. We will reach out to set you up"
                    rows={4}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border ${errors.message ? 'border-red-500' : 'border-white/20'} text-white focus:outline-none focus:ring-2 focus:ring-[#0CB4E4] focus:border-transparent resize-none disabled:opacity-50`}
                    required
                  />
                </div>
                {errors.message && <p className="mt-1 text-sm text-red-400">{errors.message}</p>}
                <p className="mt-1 text-xs text-gray-400">{formData.message.length}/1000 characters</p>
              </div>
            </div>

          

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6">
              <button 
                type="button" 
                onClick={handleClose} 
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/90 text-[#161513] rounded-lg hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center justify-center gap-2 font-medium"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#161513] border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    
                    Submit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}